import { enhancePreparedOcrImage, type OcrTextImageEnhancement, type PreparedOcrImage } from '@/core/image'
import { prepareYellowLevelImage } from './yellow-level-image'

const levelImageEnhancement: OcrTextImageEnhancement = {
  scale: 8, targetContentHeight: 120, minimumScale: 1.5, scaleStep: .25,
  padding: 3, contrast: 1, sharpen: 0, interpolation: 'smooth',
}

/** Retry order, never a per-frame rotation. Both paths preserve gray stroke edges. */
export const LEVEL_IMAGE_ENHANCEMENTS: readonly OcrTextImageEnhancement[] = [
  levelImageEnhancement,
  { ...levelImageEnhancement, interpolation: 'pixelated' },
  { ...levelImageEnhancement, targetContentHeight: 80 },
  { ...levelImageEnhancement, targetContentHeight: 96 },
]

/** Luminance survives capture chroma subsampling better than the blue channel alone. */
export function prepareLevelImage(
  prepared: PreparedOcrImage,
  enhancement: OcrTextImageEnhancement | undefined,
): PreparedOcrImage | null {
  const { source, sourceRect } = prepared
  const orange = new Uint8Array(source.width * source.height)
  let left = source.width, top = source.height, right = -1, bottom = -1, count = 0
  for (let y = 0; y < source.height; y++) for (let x = 0; x < source.width; x++) {
    const i = (y * source.width + x) * 4
    const r = source.data[i]!, g = source.data[i + 1]!, b = source.data[i + 2]!
    if (r > 180 && g > 65 && g < 200 && b < 100 && r > g + 45) {
      orange[y * source.width + x] = 1
      left = Math.min(left, x); right = Math.max(right, x)
      top = Math.min(top, y); bottom = Math.max(bottom, y)
      count++
    }
  }
  const width = right - left + 1, height = bottom - top + 1
  // Sparse warm antialias pixels also occur around yellow ink. An orange
  // background has area coverage; once present, all original guards still apply.
  if (width < 4 || height < 5 || count < width * height * .25) return prepareYellowLevelImage(prepared, enhancement)
  // A badge touching the authorized crop may be missing a digit. Never guess that digit.
  if (left <= 0 || top <= 0 || right >= source.width - 1 || bottom >= source.height - 1
    || width < 4 || height < 5 || width / height < .4 || width / height > 3.8
    || count < width * height * .25) return null

  // Disjoint badges/occlusions leave an empty column. Small gaps between digit tiles are allowed.
  let gap = 0
  for (let x = left; x <= right; x++) {
    let pixels = 0
    for (let y = top; y <= bottom; y++) pixels += orange[y * source.width + x]!
    gap = pixels ? 0 : gap + 1
    if (gap > Math.max(1, Math.floor(height * .15))) return null
  }
  // Allow a single antialiased fringe row without weakening the crop-edge guard.
  for (const band of [[top, top + 1], [bottom - 1, bottom]]) {
    const coverage = band.map(y => {
      let pixels = 0
      for (let x = left; x <= right; x++) pixels += orange[y! * source.width + x]!
      return pixels
    })
    if (Math.max(...coverage) < width * .4) return null
  }

  // Statistics exclude the white margin. Low contrast must not amplify background noise.
  const luminances: number[] = [], background: number[] = []
  for (let y = top; y <= bottom; y++) for (let x = left; x <= right; x++) {
    const i = (y * source.width + x) * 4
    const luminance = .299 * source.data[i]! + .587 * source.data[i + 1]! + .114 * source.data[i + 2]!
    luminances.push(luminance)
    if (orange[y * source.width + x]) background.push(luminance)
  }
  const low = percentile(background, .9), high = percentile([...luminances], .95)
  if (high - low < 32) return null

  const margin = 2, paddedWidth = width + margin * 2, paddedHeight = height + margin * 2
  const data = new Uint8ClampedArray(paddedWidth * paddedHeight * 4).fill(255)
  const original = new Uint8ClampedArray(width * height * 4)
  let foreground = 0
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const i = ((y + top) * source.width + x + left) * 4
    original.set(source.data.subarray(i, i + 4), (y * width + x) * 4)
    const alpha = Math.max(0, Math.min(1, (luminances[y * width + x]! - low) / (high - low)))
    if (alpha >= .5) foreground++
    const value = 255 * (1 - alpha)
    const out = ((y + margin) * paddedWidth + x + margin) * 4
    data[out] = data[out + 1] = data[out + 2] = value
  }
  if (foreground < 2 || foreground > width * height * .65) return null
  const badge = new ImageData(original, width, height)
  const mask = new ImageData(data, paddedWidth, paddedHeight)
  const enhanced = enhancePreparedOcrImage({ ...prepared, source: mask, processed: mask }, enhancement)
  const scaleX = enhanced.transform.contentWidth / paddedWidth
  const scaleY = enhanced.transform.contentHeight / paddedHeight
  return {
    source: badge,
    sourceRect: {
      x: sourceRect.x + left / source.width * sourceRect.width,
      y: sourceRect.y + top / source.height * sourceRect.height,
      width: width / source.width * sourceRect.width,
      height: height / source.height * sourceRect.height,
    },
    processed: enhanced.processed,
    transform: {
      contentX: enhanced.transform.contentX + margin * scaleX,
      contentY: enhanced.transform.contentY + margin * scaleY,
      contentWidth: width * scaleX, contentHeight: height * scaleY,
    },
  }
}

function percentile(values: number[], fraction: number): number {
  values.sort((a, b) => a - b)
  return values[Math.min(values.length - 1, Math.floor(values.length * fraction))]!
}

/** Prepared level images contain digits only. Never salvage numbers from z1, /1 or LV.20. */
export function parseLevelDigits(text: string): number | null {
  const digits = text.normalize('NFKC').replace(/\s/g, '')
  if (!/^\d{1,3}$/.test(digits)) return null
  const level = Number(digits)
  return level >= 1 && level <= 200 ? level : null
}
